import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
} from "recharts";
import ChartModal from "@/components/ChartModal";

const COLORS = {
    "2Y": "rgba(140, 185, 220, 0.65)",
    "5Y": "rgba(220, 200, 130, 0.80)",
    "10Y": "rgba(170, 180, 175, 0.60)",
    "LT": "rgba(100, 190, 140, 0.80)",
};
const COLORS_SOLID = {
    "2Y": "#8cb9dc",
    "5Y": "#dcc882",
    "10Y": "#aab4af",
    "LT": "#64be8c",
};

const DESCRIPTION =
    "This panel tracks how asset-manager positioning (Pension Funds, Insurance Companies, etc.) is distributed along the U.S. Treasury curve, showing the percentage of open interest they hold in 2Y, 5Y, 10Y, and long-term Treasury futures over a rolling 52-week window. It pairs those four time series with percentile gauges, so you can immediately see whether current curve-point exposures sit near high, low, or mid-range levels.";

const YEAR_LINE = new Date("2026-01-01").getTime();

export default function TreasuryCurveCard({ api }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let alive = true;
        axios.get(`${api}/treasury-curve`)
            .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
            .catch(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [api]);

    const chartData = useMemo(() => {
        if (!data?.terms?.length) return [];
        const dateMap = {};
        data.terms.forEach((term) => {
            term.series.forEach((pt) => {
                if (!dateMap[pt.date]) dateMap[pt.date] = { date: pt.date, t: new Date(pt.date).getTime() };
                dateMap[pt.date][term.key] = pt.pct_oi;
            });
        });
        return Object.values(dateMap).sort((a, b) => a.t - b.t);
    }, [data]);

    if (loading) {
        return <div className="pord-body py-16 text-center" style={{ fontSize: 13 }}>loading treasury curve data...</div>;
    }

    const renderChart = (height) => (
        <div style={{ width: "100%", height }}>
            {chartData.length === 0 ? (
                <div className="pord-body h-full flex items-center justify-center">no data</div>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -6 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis
                            dataKey="t"
                            type="number"
                            scale="time"
                            domain={["dataMin", "dataMax"]}
                            tickFormatter={(t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            tickLine={false}
                            axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                            minTickGap={50}
                        />
                        <YAxis
                            tickFormatter={(v) => `${v.toFixed(0)}%`}
                            tickLine={false}
                            axisLine={false}
                            width={50}
                        />
                        <Tooltip content={<CurveTooltip />} />
                        <ReferenceLine
                            x={YEAR_LINE}
                            stroke="rgba(184,212,208,0.35)"
                            strokeDasharray="4 4"
                            label={{ value: "2026", position: "insideTopRight", fill: "rgba(184,212,208,0.5)", fontSize: 10, offset: 6 }}
                        />
                        <Line type="monotone" dataKey="2Y" stroke={COLORS["2Y"]} strokeWidth={1.5} dot={false} connectNulls />
                        <Line type="monotone" dataKey="5Y" stroke={COLORS["5Y"]} strokeWidth={2} dot={false} connectNulls />
                        <Line type="monotone" dataKey="10Y" stroke={COLORS["10Y"]} strokeWidth={1.5} dot={false} connectNulls />
                        <Line type="monotone" dataKey="LT" stroke={COLORS["LT"]} strokeWidth={2} dot={false} connectNulls />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );

    const renderLegend = () => (
        <div className="flex flex-wrap gap-4 mt-4 text-xs font-light">
            {["2Y", "5Y", "10Y", "LT"].map((k) => (
                <span key={k} style={{ color: COLORS_SOLID[k] }}>
                    <span style={{ display: "inline-block", width: 14, height: 2, background: COLORS_SOLID[k], marginRight: 6, verticalAlign: "middle" }} />
                    {k}
                </span>
            ))}
        </div>
    );

    const renderGauges = () => (
        <div>
            <div className="pord-label mb-3 mt-8">52-week Percentile Rank</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data?.terms?.map((term) => (
                    <PercentileGauge
                        key={term.key}
                        termKey={term.key}
                        label={term.label}
                        current={term.current}
                        scale={term.percentile_scale}
                        color={COLORS_SOLID[term.key]}
                    />
                ))}
            </div>
            <p className="pord-body text-[12px] mt-5" style={{ color: "rgba(200,205,195,0.70)", lineHeight: 1.7 }}>
                1SD band considered between 16th percentile and 84th percentile. Over 2SD considered extreme positioning.
            </p>
        </div>
    );

    return (
        <>
            <button
                data-testid="card-0204"
                className="metric-card-frame w-full text-left cursor-pointer"
                onClick={() => setExpanded(true)}
                style={{ display: "block", transition: "border-color 200ms ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(184,212,208,0.3)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(184,212,208,0.16)"; }}
            >
                <div className="pord-eyebrow mb-3">Card 0204 · Treasury Curve · click to expand</div>
                <h2 className="pord-heading text-2xl sm:text-3xl text-white mb-3">
                    Treasury Curve Positioning — Asset Managers
                </h2>
                <div className="pord-label mb-3">52-week lookback · % OI Asset Managers</div>
                <div style={{ pointerEvents: "none" }}>
                    {renderChart(320)}
                    {renderLegend()}
                    {renderGauges()}
                </div>
            </button>

            <ChartModal open={expanded} onClose={() => setExpanded(false)} title="CARD 0204">
                <h2 className="pord-heading text-2xl sm:text-3xl text-white mb-3">
                    Treasury Curve Positioning — Asset Managers
                </h2>
                <p className="pord-body mb-6 max-w-3xl" style={{ fontSize: 15, lineHeight: 1.85, color: "rgba(200,205,195,0.82)" }}>
                    {DESCRIPTION}
                </p>
                <div className="pord-label mb-3">52-week lookback · % OI Asset Managers</div>
                {renderChart(460)}
                {renderLegend()}
                {renderGauges()}
            </ChartModal>
        </>
    );
}

function PercentileGauge({ termKey, label, current, scale, color }) {
    const rank = current?.pct_rank_52w;
    const pctOi = current?.pct_oi;
    const p5 = scale?.p5;
    const p16 = scale?.p16;
    const p50 = scale?.p50;
    const p84 = scale?.p84;
    const p95 = scale?.p95;

    return (
        <div
            data-testid={`gauge-${termKey}`}
            className="rounded-xl px-5 py-4 border"
            style={{ backgroundColor: "rgba(255,255,255,0.025)", borderColor: "rgba(255,255,255,0.06)" }}
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-light text-white">{label} ({termKey})</span>
                <span className="text-xl font-light tabular-nums" style={{ color }}>
                    {rank != null ? `${Math.round(rank)}th` : "\u2014"} <span className="text-xs" style={{ color: "var(--pord-text-muted)" }}>pctile</span>
                    {pctOi != null && (
                        <span className="text-xs font-light tabular-nums ml-2" style={{ color: "rgba(200,205,195,0.65)" }}>
                            {fmtV(pctOi)}% OI
                        </span>
                    )}
                </span>
            </div>

            {/* Gauge bar */}
            <div className="relative h-2 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                {rank != null && (
                    <div
                        className="absolute top-1/2 rounded-full"
                        style={{
                            left: `${Math.min(Math.max(rank, 2), 98)}%`,
                            width: 12,
                            height: 12,
                            background: color,
                            transform: "translate(-50%, -50%)",
                            boxShadow: `0 0 0 3px rgba(255,255,255,0.08)`,
                        }}
                    />
                )}
                {[5, 16, 50, 84, 95].map((p) => (
                    <div
                        key={p}
                        className="absolute top-0 bottom-0"
                        style={{
                            left: `${p}%`,
                            width: 1,
                            background: p === 50 ? "rgba(255,255,255,0.20)" : "rgba(255,255,255,0.10)",
                        }}
                    />
                ))}
            </div>

            {/* Bottom labels: positioned to match tick marks */}
            <div className="relative mt-2" style={{ height: 28 }}>
                {[
                    { pct: 5, label: "5th", value: p5 },
                    { pct: 16, label: "16th", value: p16, prefix: "1SD " },
                    { pct: 50, label: "50th", value: p50 },
                    { pct: 84, label: "84th", value: p84, prefix: "1SD " },
                    { pct: 95, label: "95th", value: p95 },
                ].map((item) => (
                    <div
                        key={item.pct}
                        className="absolute text-center"
                        style={{
                            left: `${item.pct}%`,
                            transform: "translateX(-50%)",
                            width: 70,
                        }}
                    >
                        <div className="text-[10px] font-light tabular-nums" style={{ color: "rgba(200,205,195,0.70)" }}>
                            {item.prefix || ""}{item.value != null ? `${fmtV(item.value)}%` : ""}
                        </div>
                        <div className="text-[9px] font-light" style={{ color: "var(--pord-text-dim)" }}>
                            {item.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CurveTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
        <div className="pord-surface px-4 py-3 text-xs" style={{ background: "rgba(26,29,18,0.92)" }}>
            <div className="pord-label mb-1">{p.date}</div>
            {["2Y", "5Y", "10Y", "LT"].map((k) =>
                p[k] != null ? (
                    <div key={k} style={{ color: COLORS_SOLID[k] }}>{k}: {p[k] > 0 ? "+" : ""}{p[k].toFixed(2)}%</div>
                ) : null
            )}
        </div>
    );
}

function fmtV(v) {
    if (v == null) return "";
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(1)}`;
}